import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import IntegrationLogo from "@/pages/settings/components/integrations/IntegrationLogo";
import {
  INTEGRATIONS_TRANSLATION_PREFIX,
  Integration
} from "@/pages/settings/components/integrations/integrations";

interface IntegrationCardProps {
  integration: Integration;
  isConnected: boolean;
  isLoading: boolean;
  isError: boolean;
  onOpen: () => void;
  onConnect: () => void;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  integration,
  isConnected,
  isLoading,
  isError,
  onOpen,
  onConnect
}) => {
  const { t } = useTranslation();
  const Icon = integration.icon;

  const handleActionClick = () => {
    if (isConnected) {
      onOpen();
    } else {
      onConnect();
    }
  };

  const name = t(
    `${INTEGRATIONS_TRANSLATION_PREFIX}.items.${integration.id}.name`
  );

  // The open control is an empty overlay covering the whole card, so the card content stays outside it and the action button stays a sibling rather than a nested interactive element.
  return (
    <div className="relative flex min-w-[240px] flex-col justify-between gap-2 overflow-hidden rounded-2xl border-[0.5px] border-border-widget-card bg-surface-widget-card p-4 shadow-[0_6px_6px_0_rgba(12,17,29,0.16)] transition-colors hover:bg-surface-widget-card-hover">
      <button
        type="button"
        onClick={onOpen}
        aria-label={name}
        data-test={`integration-card-${integration.id}`}
        className="absolute inset-0 rounded-2xl focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
      />
      <div className="pointer-events-none relative flex flex-col gap-2">
        <IntegrationLogo icon={<Icon />} showConnectedBadge={isConnected} />
        <div className="flex flex-col gap-1 px-1">
          <Text variant="lgBody" className="font-medium">
            {name}
          </Text>
          <Text variant="bodySecondary">
            {t(
              `${INTEGRATIONS_TRANSLATION_PREFIX}.items.${integration.id}.description`
            )}
          </Text>
          {isError && (
            <Text
              variant="smBodyError"
              data-test={`integration-card-error-${integration.id}`}
            >
              {t(`${INTEGRATIONS_TRANSLATION_PREFIX}.statusError`)}
            </Text>
          )}
        </div>
      </div>
      <div className="relative z-10 flex justify-end">
        <Button
          variant="secondary"
          disabled={isLoading || isError}
          onClick={handleActionClick}
          data-test={`integration-card-action-${integration.id}`}
          className="px-[6px] py-1"
        >
          {t(
            `${INTEGRATIONS_TRANSLATION_PREFIX}.${isConnected ? "manage" : "connect"}`
          )}
        </Button>
      </div>
    </div>
  );
};

export default IntegrationCard;
